import { useState } from 'react'
import AuthenticatedTemplate from './components/AuthenticatedTemplate'
import UserProfile from './components/UserProfile'
import ReportsList from './components/ReportsList'
import AdminManagement from './components/AdminManagement'
import UserManagement from './components/UserManagement'
import SubscriptionInfo from './components/SubscriptionInfo'
import CustomerEmbeddingManagement from './components/CustomerEmbeddingManagement'
import DeleteReportConfirmation from './components/DeleteReportConfirmation'
import DeleteProgressPopup from './components/DeleteProgressPopup'
import UploadProgressPopup from './components/UploadProgressPopup'
import OverwriteReportConfirmation from './components/OverwriteReportConfirmation'
import AssignReportPopup from './components/AssignReportPopup'
import SubscriptionSelector from './components/SubscriptionSelector'
import ValidationErrorModal from './components/ValidationErrorModal'
import { useAuth } from './hooks/useAuth'
import { useAdminData } from './hooks/useAdminData'
import { useReportManagement } from './hooks/useReportManagement'
import { useMessage } from './hooks/useMessage'
import { useIdentitySync } from './hooks/useIdentitySync'
import { useUserManagement } from './hooks/useUserManagement'
import './App.css'
import './components/CustomerEmbeddingManagement.css'


function App() {
  const { getUserEmail, getUserDisplayName } = useAuth();
  const { 
    subscriptionData, 
    assignedWorkspace, 
    availableSubscriptions,
    loading: dataLoading, 
    error: dataError, 
    loadSubscriptionData,
    loadAvailableSubscriptions
  } = useAdminData();
  const { message, showMessage } = useMessage();
  
  // Tab state for switching between different views
  // Default to 'reports' since Reports tab should be shown first
  const [activeTab, setActiveTab] = useState('reports');
  
  // Subscription selection state
  const [selectedSubscriptionId, setSelectedSubscriptionId] = useState(null);
  const [showSubscriptionSelector, setShowSubscriptionSelector] = useState(false);
  
  // Delete confirmation modal state
  const [deleteModalState, setDeleteModalState] = useState({ 
    show: false, 
    reportId: null, 
    reportName: null 
  });
  
  // Store report name separately for progress display
  const [deletingReportName, setDeletingReportName] = useState(null);
  
  // Store report name for upload progress display
  const [uploadingReportName, setUploadingReportName] = useState(null);
  
  // Overwrite confirmation modal state
  const [overwriteModalState, setOverwriteModalState] = useState({ 
    show: false, 
    reportName: null 
  });
  
  // Assign report popup state
  const [assignReportState, setAssignReportState] = useState({
    show: false,
    reportName: null,
    reportId: null
  });
  
  // Validation error modal state
  const [validationErrorState, setValidationErrorState] = useState({
    show: false,
    title: '',
    message: ''
  });
  
  const { 
    selectedFile, 
    setSelectedFile, 
    loading: reportLoading,
    deleteProgress,
    uploadProgress,
    uploadReport, 
    deleteReport
  } = useReportManagement(assignedWorkspace, null);
  
  // Initialize user management hook for report assignment
  const { 
    assignReports, 
    loading: userLoading 
  } = useUserManagement(subscriptionData, null);

  // Automatically sync OIDC identity claims on first login
  useIdentitySync(subscriptionData);

  // Handle subscription selection
  const handleSelectSubscription = async (subscriptionId) => {
    setSelectedSubscriptionId(subscriptionId);
    setShowSubscriptionSelector(false);
    
    try {
      await loadSubscriptionData(subscriptionId);
      // Silently load subscription without showing success message
    } catch (error) {
      showMessage(`Failed to load subscription: ${error.message}`, 'error');
    }
  };

  // Determine if we should show subscription selector
  // Show if: availableSubscriptions is loaded AND has multiple subscriptions AND no subscription is selected yet
  const shouldShowSubscriptionSelector = availableSubscriptions && 
                                          availableSubscriptions.length > 1 && 
                                          !subscriptionData;


  const handleUpload = async () => {
    try {
      // Store the report name before upload starts
      if (selectedFile) {
        setUploadingReportName(selectedFile.name.replace(/\.pbix$/i, ''));
      }
      const result = await uploadReport(subscriptionData);
      
      // Result now contains: { message, reportId, reportName, isOverwrite }
      // No success message - let the UI update speak for itself
      setSelectedFile(null);
      
      // Reload data to get the new report
      await loadSubscriptionData(selectedSubscriptionId);
      
      // Show assignment popup only for NEW uploads (not overwrites) and if users exist
      if (result.reportId && !result.isOverwrite && subscriptionData?.users && subscriptionData.users.length > 0) {
        setAssignReportState({
          show: true,
          reportName: result.reportName,
          reportId: result.reportId
        });
      }
    } catch (error) {
      // Check if this is a duplicate report error
      if (error.message === 'DUPLICATE_REPORT') {
        setOverwriteModalState({
          show: true,
          reportName: error.reportName
        });
        // Rethrow so the upload modal knows to stay open
        throw error;
      } else if (error.message.includes('exceeds your plan limit') || error.message.includes('reached your plan limit')) {
        // Show validation errors in a modal popup
        const isReportCount = error.message.includes('reached your plan limit');
        setValidationErrorState({
          show: true,
          title: isReportCount ? 'Report Limit Reached' : 'File Size Limit Exceeded',
          message: error.message
        });
        // Don't rethrow - just show the modal
      } else {
        showMessage(`Upload failed: ${error.message}`, 'error');
        throw error;
      }
    } finally {
      // Clear the uploading report name after a delay
      setTimeout(() => setUploadingReportName(null), 1500);
    }
  };

  const handleConfirmOverwrite = async () => {
    // Close modal
    setOverwriteModalState({ show: false, reportName: null });
    
    try {
      // Store the report name before upload starts
      if (selectedFile) {
        setUploadingReportName(selectedFile.name.replace(/\.pbix$/i, ''));
      }
      const result = await uploadReport(subscriptionData, true); // true = overwrite
      
      // Result now contains: { message, reportId, reportName, isOverwrite }
      // No success message - let the UI update speak for itself
      setSelectedFile(null);
      
      // Reload data but don't show assignment popup for overwrites
      // (assignments are preserved by reportID since it's the same report)
      await loadSubscriptionData(selectedSubscriptionId);
    } catch (error) {
      showMessage(`Overwrite failed: ${error.message}`, 'error');
    } finally {
      // Clear the uploading report name after a delay
      setTimeout(() => setUploadingReportName(null), 1500);
    }
  };

  const handleCancelOverwrite = () => {
    setOverwriteModalState({ show: false, reportName: null });
  };

  const handleDeleteReport = async (reportId) => {
    // Find the report to get its name
    const report = assignedWorkspace?.reports?.find(r => 
      (r.ReportID === reportId || r.reportID === reportId)
    );
    
    if (!report) {
      showMessage('Report not found', 'error');
      return;
    }

    const reportName = report.ReportName || report.reportName;
    
    // Show the delete confirmation modal
    setDeleteModalState({
      show: true,
      reportId: reportId,
      reportName: reportName
    });
  };

  const handleConfirmDelete = async () => {
    const { reportId, reportName } = deleteModalState;
    
    // Store report name for progress display
    setDeletingReportName(reportName);
    
    // Close modal
    setDeleteModalState({ show: false, reportId: null, reportName: null });
    
    try {
      // Always delete both report and dataset
      const result = await deleteReport(reportId, subscriptionData, true);
      
      // No success message - let the UI update speak for itself
      loadSubscriptionData(selectedSubscriptionId);
    } catch (error) {
      showMessage(`Delete failed: ${error.message}`, 'error');
    } finally {
      // Clear the deleting report name after a delay
      setTimeout(() => setDeletingReportName(null), 1500);
    }
  };

  const handleCancelDelete = () => {
    setDeleteModalState({ show: false, reportId: null, reportName: null });
  };
  
  // Handle assigning report to users from the popup
  const handleAssignReportToUsers = async (userIds, reportIds) => {
    try {
      // Assign the report to each selected user
      for (const userId of userIds) {
        await assignReports(userId, reportIds);
      }
      
      // Close the popup and refresh data
      setAssignReportState({ show: false, reportName: null, reportId: null });
      loadSubscriptionData(selectedSubscriptionId);
    } catch (error) {
      showMessage(`Failed to assign report: ${error.message}`, 'error');
    }
  };
  
  // Handle skipping report assignment
  const handleSkipAssignment = () => {
    setAssignReportState({ show: false, reportName: null, reportId: null });
  };



  const loading = dataLoading || reportLoading;

  // Check if subscription is suspended
  const isSuspended = subscriptionData?.saasSubscriptionStatus === 'Suspended' || 
                      subscriptionData?.SaasSubscriptionStatus === 'Suspended';

  // Tab configuration - Reports first, Users second. Admins moved to after Subscription.
  const tabs = [
    { id: 'reports', label: 'Reports' },
    { id: 'users', label: 'Users' },
    { id: 'embedding', label: 'Embedding' },
    { id: 'subscription', label: 'Subscription' },
    { id: 'admins', label: 'Admins' },
    { id: 'help', label: 'Help' }
  ];

  return (
    <AuthenticatedTemplate>
      {/* Show loading popup overlay while fetching initial data */}
      {!subscriptionData && !dataError && dataLoading && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2000,
          backdropFilter: 'blur(4px)'
        }}>
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '2.5rem',
            boxShadow: '0 10px 40px rgba(0, 0, 0, 0.3)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '1.5rem',
            minWidth: '300px'
          }}>
            <div 
              className="spinner-border text-primary" 
              role="status" 
              style={{ width: '3rem', height: '3rem' }}
            >
              <span className="visually-hidden">Loading...</span>
            </div>
            <p style={{ 
              fontSize: '1.1rem', 
              color: '#495057',
              margin: 0,
              fontWeight: '500'
            }}>
              Loading Subscription...
            </p>
          </div>
        </div>
      )}

      {/* Show subscription selector if user has multiple subscriptions and either none selected OR switch button clicked */}
      {(shouldShowSubscriptionSelector || showSubscriptionSelector) && availableSubscriptions && availableSubscriptions.length > 1 && !dataLoading && (
        <SubscriptionSelector
          subscriptions={availableSubscriptions}
          onSelect={handleSelectSubscription}
          loading={dataLoading}
        />
      )}

      {/* Auto-load single subscription */}
      {availableSubscriptions && availableSubscriptions.length === 1 && !subscriptionData && !dataLoading && (
        (() => {
          // Auto-select the single subscription
          const singleSubId = availableSubscriptions[0].SubscriptionID || availableSubscriptions[0].subscriptionID;
          handleSelectSubscription(singleSubId);
          return null;
        })()
      )}

      <div className="container">
        <div className="header" style={{ 
          marginBottom: '1rem', 
          padding: '1.5rem 0',
          background: '#f8f9fa',
          borderBottom: '2px solid #e0e0e0'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', paddingLeft: '2rem' }}>
              <img src="/images/MerlinLogo250pxTransparent.png" alt="MerlinIQ Logo" style={{ height: '60px', width: 'auto' }} />
              <h1 style={{ 
                margin: '0', 
                fontSize: '1.5rem', 
                color: '#1B394B',
                fontWeight: '700',
                background: 'none',
                WebkitBackgroundClip: 'unset',
                WebkitTextFillColor: 'unset',
                backgroundClip: 'unset',
                textShadow: '0 1px 2px rgba(27, 57, 75, 0.08)'
              }}>
                MerlinIQ Management Portal
              </h1>
              {/* Show "Switch Subscription" button if user has multiple subscriptions */}
              {availableSubscriptions && availableSubscriptions.length > 1 && (
                <button
                  onClick={() => setShowSubscriptionSelector(true)}
                  style={{
                    padding: '0.5rem 1rem',
                    background: '#3A6A92',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '0.9rem',
                    fontWeight: '500',
                    color: 'white',
                    marginLeft: '1rem'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = '#2f5770'}
                  onMouseLeave={(e) => e.currentTarget.style.background = '#3A6A92'}
                >
                  Switch Subscription
                </button>
              )}
            </div>
            <div style={{ paddingRight: '2rem' }}>
              <UserProfile />
            </div>
          </div>
        </div>

        <div className="content">
          {message.text && (
            <div className={`alert alert-${message.type}`}>
              {message.text}
            </div>
          )}

          {/* Show API/Data errors */}
          {dataError && (
            <div className="alert alert-error" style={{ 
              background: '#fee', 
              border: '1px solid #fcc', 
              padding: '2rem', 
              marginBottom: '1rem',
              borderRadius: '12px',
              textAlign: 'center'
            }}>
              {dataError === 'NO_SUBSCRIPTION_FOUND' ? (
                <>
                  <div style={{ fontSize: '3rem', marginBottom: '1rem', color: '#0078d4' }}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" fill="currentColor" viewBox="0 0 16 16" style={{ display: 'block', margin: '0 auto' }}>
                      <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z"/>
                      <path d="M8 4a.905.905 0 0 0-.9.995l.35 3.507a.552.552 0 0 0 1.1 0l.35-3.507A.905.905 0 0 0 8 4zm.002 6a1 1 0 1 0 0 2 1 1 0 0 0 0-2z"/>
                    </svg>
                  </div>
                  <h2 style={{ margin: '0 0 1rem 0', color: '#212529' }}>
                    Welcome to MerlinIQ!
                  </h2>
                  <p style={{ margin: '0 0 1.5rem 0', fontSize: '1.1rem', color: '#495057' }}>
                    You've successfully signed in with your Microsoft account, but you don't have an active subscription yet.
                  </p>
                  <p style={{ margin: '0 0 1.5rem 0', fontSize: '1rem', color: '#6c757d' }}>
                    To get started with MerlinIQ Reporting, visit our website to sign up for a subscription.
                  </p>
                  <a 
                    href="https://www.merliniq.cloud" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="btn btn-primary"
                    style={{ 
                      fontSize: '1.1rem', 
                      padding: '0.75rem 2rem',
                      textDecoration: 'none',
                      display: 'inline-block',
                      marginTop: '0.5rem'
                    }}
                  >
                    Visit MerlinIQ.cloud to Sign Up
                  </a>
                </>
              ) : dataError === 'AUTHENTICATION_EXPIRED' ? (
                <>
                  <div style={{ fontSize: '3rem', marginBottom: '1rem', color: '#ffc107' }}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" fill="currentColor" viewBox="0 0 16 16" style={{ display: 'block', margin: '0 auto' }}>
                      <path d="M8 1a2 2 0 0 1 2 2v4H6V3a2 2 0 0 1 2-2zm3 6V3a3 3 0 0 0-6 0v4a2 2 0 0 0-2 2v5a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2z"/>
                    </svg>
                  </div>
                  <h2 style={{ margin: '0 0 1rem 0', color: '#856404' }}>
                    Session Expired
                  </h2>
                  <p style={{ margin: '0 0 1.5rem 0', fontSize: '1.1rem', color: '#856404' }}>
                    Your login session has expired. Please sign out and sign back in to continue.
                  </p>
                  <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', marginTop: '1rem' }}>
                    <UserProfile showLogoutOnly={true} />
                  </div>
                </>
              ) : (
                <>
                  <div style={{ fontSize: '3rem', marginBottom: '1rem', color: '#dc3545' }}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" fill="currentColor" viewBox="0 0 16 16" style={{ display: 'block', margin: '0 auto' }}>
                      <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0zM8 4a.905.905 0 0 0-.9.995l.35 3.507a.552.552 0 0 0 1.1 0l.35-3.507A.905.905 0 0 0 8 4zm.002 6a1 1 0 1 0 0 2 1 1 0 0 0 0-2z"/>
                    </svg>
                  </div>
                  <h2 style={{ margin: '0 0 1rem 0', color: '#721c24' }}>
                    Error Loading Data
                  </h2>
                  <p style={{ margin: '0 0 1.5rem 0', fontSize: '1.1rem', color: '#721c24' }}>
                    <strong>{dataError}</strong>
                  </p>
                  <button 
                    className="btn btn-primary" 
                    onClick={() => loadSubscriptionData(selectedSubscriptionId)}
                    style={{ marginTop: '10px' }}
                  >
                    Retry Loading Data
                  </button>
                </>
              )}
            </div>
          )}

          {/* Tab Navigation */}
          {subscriptionData && (
            <div className="tabs-container" style={{ marginBottom: '2rem' }}>
              <div className="tabs-header" style={{ display: 'flex', borderBottom: '2px solid #ddd' }}>
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    className={`tab-button ${activeTab === tab.id ? 'active' : ''}`}
                    onClick={() => setActiveTab(tab.id)}
                    style={{
                      padding: '1rem 1.5rem',
                      border: 'none',
                      background: activeTab === tab.id ? '#3A6A92' : 'transparent',
                      color: activeTab === tab.id ? 'white' : '#333',
                      cursor: 'pointer',
                      borderBottom: activeTab === tab.id ? '2px solid #3A6A92' : '2px solid transparent',
                      fontSize: '1rem',
                      fontWeight: activeTab === tab.id ? 'bold' : 'normal'
                    }}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
              
              <div className="tabs-content" style={{ paddingTop: '1rem' }}>
                {/* Reports & Workspace Tab */}
                {activeTab === 'reports' && (
                  <div className="reports-tab">
                    <ReportsList 
                      reports={assignedWorkspace?.reports || []}
                      workspaceId={assignedWorkspace?.workspaceID}
                      onDeleteReport={handleDeleteReport}
                      loading={loading}
                      selectedFile={selectedFile}
                      onFileSelect={setSelectedFile}
                      onUpload={handleUpload}
                      workspaceName={assignedWorkspace?.workspaceName}
                      subscriptionData={subscriptionData}
                      isSuspended={isSuspended}
                    />

                    {/* No workspace/data messages */}
                    {!loading && !dataError && !assignedWorkspace && (
                      <div className="section">
                        <h2>No Workspace Access</h2>
                        <p>You don't have access to any Power BI workspaces. Please contact your administrator.</p>
                        <button 
                          className="btn btn-primary" 
                          onClick={() => loadSubscriptionData(selectedSubscriptionId)}
                        >
                          Retry Loading Data
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {/* Admin Management Tab */}
                {activeTab === 'admins' && (
                  <div className="admins-tab">
                    <AdminManagement
                      subscriptionData={subscriptionData}
                      onRefresh={() => loadSubscriptionData(selectedSubscriptionId)}
                      showMessage={showMessage}
                      isSuspended={isSuspended}
                    />
                  </div>
                )}

                {/* User Management Tab */}
                {activeTab === 'users' && (
                  <div className="users-tab">
                    <UserManagement
                      subscriptionData={subscriptionData}
                      onRefresh={() => loadSubscriptionData(selectedSubscriptionId)}
                      showMessage={showMessage}
                    />
                  </div>
                )}

                {/* Customer Embedding Tab */}
                {activeTab === 'embedding' && (
                  <div className="embedding-tab">
                    <CustomerEmbeddingManagement
                      subscriptionData={subscriptionData}
                      isSuspended={isSuspended}
                    />
                  </div>
                )}

                {/* Subscription Tab */}
                {activeTab === 'subscription' && (
                  <div className="subscription-tab">
                    <SubscriptionInfo
                      subscriptionData={subscriptionData}
                    />
                  </div>
                )}

                {/* Help Tab */}
                {activeTab === 'help' && (
                  <div className="help-tab">
                    <div className="section">
                      <h2>User Guide</h2>
                      
                      <div style={{ marginBottom: '2rem' }}>
                        <h3>Understanding Your Subscription</h3>
                        <ul style={{ fontSize: '0.9rem', lineHeight: '1.6' }}>
                          {(() => {
                            const billingModel = subscriptionData?.billingModel || subscriptionData?.BillingModel || 'PerUser';
                            const isPerUser = billingModel === 'PerUser';
                            
                            return (
                              <>
                                <li><strong>Your Billing Model:</strong> {isPerUser ? 'Per-User Billing' : 'Flat-Rate Plan'}</li>
                                {isPerUser ? (
                                  <li><strong>Per-User Billing:</strong> License count is managed through your Microsoft 365 subscription quantity. Add more licenses via the Microsoft Admin Center</li>
                                ) : (
                                  <li><strong>Flat-Rate Billing:</strong> Fixed number of licenses included with your plan. To increase limits, upgrade your plan in the Azure Portal</li>
                                )}
                                {availableSubscriptions && availableSubscriptions.length > 1 && (
                                  <li><strong>Multiple Subscriptions:</strong> Use the "Switch Subscription" button to change between subscriptions</li>
                                )}
                              </>
                            );
                          })()}
                        </ul>
                      </div>

                      <div style={{ marginBottom: '2rem' }}>
                        <h3>Administrator Management</h3>
                        <ul style={{ fontSize: '0.9rem', lineHeight: '1.6' }}>
                          <li><strong>Administrator Accounts:</strong> Administrators must have a Microsoft organizational account (work or school account)</li>
                          <li><strong>Adding Admins:</strong> Click the "+ Add Admin" button and enter the admin's email and display name</li>
                          <li><strong>Display Name:</strong> If adding an admin who is already a user, the system will detect name mismatches and prompt you to use the existing name</li>
                          <li><strong>Admin Permissions:</strong> All admins have full access to manage users, reports, admins, and subscription settings</li>
                          <li><strong>Self-Deletion Prevention:</strong> You cannot delete yourself - another admin must remove your access</li>
                          <li><strong>Last Admin Protection:</strong> At least one admin must remain active in each subscription</li>
                          <li><strong>Report Count:</strong> The Admins tab shows how many reports each admin has uploaded</li>
                          <li><strong>Current User Indicator:</strong> Your own admin account is highlighted with a "(You)" label</li>
                          <li><strong>Search Functionality:</strong> Use the search box to filter admins by name or email</li>
                          <li><strong>Identity Sync:</strong> Admin identity information (OIDC claims) is automatically synced on first login</li>
                        </ul>
                      </div>

                      <div style={{ marginBottom: '2rem' }}>
                        <h3>User Management & Licensing</h3>
                        <ul style={{ fontSize: '0.9rem', lineHeight: '1.6' }}>
                          <li><strong>Adding Users:</strong> Click "+ Add User" and enter the user's email address and display name to create their account</li>
                          <li><strong>User License Summary:</strong> View purchased, active, and available licenses at the top of the Users tab</li>
                          {(() => {
                            const billingModel = subscriptionData?.billingModel || subscriptionData?.BillingModel || 'PerUser';
                            const isPerUser = billingModel === 'PerUser';
                            
                            if (isPerUser) {
                              return (
                                <>
                                  <li><strong>License Management:</strong> Your licenses are managed through your Microsoft 365 subscription quantity</li>
                                  <li><strong>Adding Licenses:</strong> Purchase additional licenses from the Microsoft Admin Center to increase capacity</li>
                                </>
                              );
                            } else {
                              return (
                                <li><strong>Purchased Licenses:</strong> Shows additional licenses purchased beyond your plan's base allocation</li>
                              );
                            }
                          })()}
                          <li><strong>Active Licenses:</strong> Displays the number of users currently assigned an active license (color-coded by usage: green/yellow/red)</li>
                          <li><strong>Available Licenses:</strong> Shows remaining licenses that can be assigned to users</li>
                          <li><strong>Managing Users:</strong> Click the "Manage" button on any user to access license controls and report assignments</li>
                          <li><strong>Activating Licenses:</strong> Users must have an active license to access their assigned reports</li>
                          <li><strong>Deactivating Licenses:</strong> Freeing a license makes it available for another user without losing report assignments</li>
                          <li><strong>At License Limit:</strong> You must deactivate an existing license or purchase more to add new active users</li>
                          <li><strong>Deleting Users:</strong> Permanently removes the user and all their report assignments</li>
                          <li><strong>License Alerts:</strong> The summary shows color-coded warnings when approaching or at your license limit</li>
                          <li><strong>Search Functionality:</strong> Use the search box to filter users by name or email</li>
                          <li><strong>User Details:</strong> The Users tab displays user name, email, license status, and report count</li>
                        </ul>
                      </div>

                      <div style={{ marginBottom: '2rem' }}>
                        <h3>Report Management & Limits</h3>
                        <ul style={{ fontSize: '0.9rem', lineHeight: '1.6' }}>
                          <li><strong>Report License Summary:</strong> View purchased, active, and available report slots in the Reports tab</li>
                          <li><strong>Purchased Reports:</strong> Shows additional report slots purchased beyond your plan's base allocation</li>
                          <li><strong>Active Reports:</strong> Displays current number of uploaded reports (color-coded by usage)</li>
                          <li><strong>Available Slots:</strong> Shows remaining report slots for new uploads</li>
                          <li><strong>Max Report Size:</strong> Displays the maximum file size allowed per report upload (varies by plan)</li>
                          <li><strong>Uploading Reports:</strong> Only administrators can upload PBIX files to the workspace via the Upload Report button</li>
                          <li><strong>Report Count Limits:</strong> Your plan may have a maximum number of reports you can upload</li>
                          <li><strong>At Report Limit:</strong> Delete an existing report or upgrade your plan to upload more</li>
                          <li><strong>Size Validation:</strong> Uploads are automatically checked against your plan's size limit before processing</li>
                          <li><strong>Refreshing Reports:</strong> To update a report with new data or changes, simply upload a new version with the same name. The system will prompt you to confirm overwriting the existing report. The new version will replace the current one while preserving all user assignments</li>
                          <li><strong>Overwriting Reports:</strong> Re-uploading a report with the same name prompts a confirmation before overwriting the existing one</li>
                          <li><strong>Deleting Reports:</strong> Permanently removes both the report and its dataset - ensure you have backups</li>
                          <li><strong>Report URLs:</strong> Each report has a unique viewer URL displayed in the Reports tab</li>
                          <li><strong>Copying URLs:</strong> Use the "Copy" button next to each report URL to share with users</li>
                          <li><strong>Searching Reports:</strong> Use the search box to filter reports by name or ID</li>
                        </ul>
                      </div>

                      <div style={{ marginBottom: '2rem' }}>
                        <h3>Report Assignment</h3>
                        <ul style={{ fontSize: '0.9rem', lineHeight: '1.6' }}>
                          <li><strong>Accessing Assignment:</strong> Click "Manage" on any user in the Users tab to open the user management popup</li>
                          <li><strong>Popup Tabs:</strong> The management popup has three tabs: Details, Assign Reports, and Unassign Reports</li>
                          <li><strong>Assigning Reports:</strong> In the "Assign Reports" tab, select one or more reports from the available list and click assign</li>
                          <li><strong>Multiple Reports:</strong> Users can be assigned multiple reports from your workspace</li>
                          <li><strong>License Requirement:</strong> Users must have an active license to view their assigned reports</li>
                          <li><strong>After Upload:</strong> When uploading a new report, you can immediately assign it to users via an automatic assignment popup</li>
                          <li><strong>Preserving Assignments:</strong> Report assignments persist when licenses are deactivated or reports are overwritten (by matching report ID)</li>
                          <li><strong>Removing Assignments:</strong> Use the "Unassign Reports" tab in the user management popup to remove specific reports</li>
                          <li><strong>Bulk Actions:</strong> Select multiple reports at once for batch assignment or removal</li>
                          <li><strong>Assignment Count:</strong> The Users tab displays how many reports are assigned to each user</li>
                        </ul>
                      </div>

                      <div style={{ marginBottom: '2rem' }}>
                        <h3>How Users Access Their Reports</h3>
                        <ul style={{ fontSize: '0.9rem', lineHeight: '1.6' }}>
                          <li><strong>Direct Report URL:</strong> Provide the Report URL from the Reports tab directly to your users. This opens the report in a full-screen browser window with Export and Personal Bookmark features enabled</li>
                          <li><strong>App Viewer URL:</strong> Provide users with the App Viewer URL (accessible via the "Open Viewer App" button in the Reports tab). This displays a simple, unbranded portal listing all reports assigned to the user. Users can copy URLs or open reports from the list. Reports opened this way include Export and Personal Bookmark features</li>
                          <li><strong>Embedded Reports:</strong> Use the Embed functionality (accessible via the "Embed" button in the Reports tab) to directly embed reports into your own applications as configured in the Embedding tab. Note that embedded reports do not provide Export or Personal Bookmark features, as these are application add-ons. However, you could develop your own solutions for these features</li>
                          <li><strong>Refreshing Reports:</strong> To update a report with new data or changes, simply upload a new version with the same name. The system will prompt you to confirm overwriting the existing report. The new version will replace the current one while preserving all user assignments</li>
                        </ul>
                      </div>

                      <div style={{ marginBottom: '2rem' }}>
                        <h3>Embedding</h3>
                        <ul style={{ fontSize: '0.9rem', lineHeight: '1.6' }}>
                          <li><strong>What is Embedding?</strong> Customer embedding allows you to integrate Power BI reports directly into your own applications, websites, or portals - giving your end users seamless access to reports without leaving your platform</li>
                          <li><strong>Use Cases:</strong> White-label analytics portals, customer-facing dashboards, integrated reporting in SaaS applications, mobile app analytics</li>
                          <li><strong>What You Can Do:</strong> Embed full reports, apply filters programmatically, control navigation and UI elements, implement single sign-on (SSO), customize branding and themes</li>
                          <li><strong>Enable Embedding:</strong> Toggle the embedding feature on/off for your subscription in the Embedding tab</li>
                          <li><strong>App Registration:</strong> Create an Entra ID app registration to get OAuth2 credentials for secure authentication</li>
                          <li><strong>Client Credentials:</strong> View your Client ID and regenerate Client Secrets - store them securely (Azure Key Vault, AWS Secrets Manager)</li>
                          <li><strong>Secret Expiration:</strong> Client secrets expire automatically - the system displays warnings when approaching expiration</li>
                          <li><strong>Secret Rotation:</strong> Regenerate client secrets regularly (every 6-12 months) or when approaching expiration for enhanced security</li>
                          <li><strong>CORS Origins:</strong> Add domains (origins) that are allowed to call the embed API from your applications</li>
                          <li><strong>Security Best Practice:</strong> Never expose client secrets in browser code - always call the embed token API from your backend server</li>
                          <li><strong>Backend Proxy Pattern:</strong> Your backend authenticates with OAuth2, then calls the embed API on behalf of your users</li>
                          <li><strong>OAuth2 Flow:</strong> Your app gets an access token from Entra ID, then uses it to request embed tokens for specific reports and users</li>
                          <li><strong>API Documentation:</strong> Find API endpoint URLs, tenant ID, authentication scopes, and token endpoints in the Embedding tab</li>
                          <li><strong>Integration Examples:</strong> Download complete guides for .NET, Node.js, Python, and JavaScript/HTML implementations with step-by-step instructions</li>
                          <li><strong>Power BI JavaScript SDK:</strong> Use Microsoft's official SDK to embed reports with full interactivity (filtering, navigation, bookmarks, etc.)</li>
                          <li><strong>Token Expiration:</strong> Embed tokens are valid for approximately 1 hour - implement token refresh logic in your application</li>
                        </ul>
                      </div>

                      <div style={{ marginBottom: '2rem' }}>
                        <h3>Row Level Security (RLS) with USERPRINCIPALNAME()</h3>
                        <ul style={{ fontSize: '0.9rem', lineHeight: '1.6' }}>
                          <li><strong>What is Row Level Security?</strong> RLS allows you to restrict data access at the row level based on the user viewing the report, ensuring each user only sees data they're authorized to access</li>
                          <li><strong>Automatic Identity Passing:</strong> This application automatically passes the user's email address as the effective identity when embedding reports, enabling seamless RLS integration</li>
                          <li><strong>USERPRINCIPALNAME() Function:</strong> In Power BI Desktop, use the DAX function USERPRINCIPALNAME() in your RLS roles to reference the logged-in user's email address</li>
                          <li><strong>Example Use Case:</strong> If your data has an "Email" column, create an RLS role with the filter: [Email] = USERPRINCIPALNAME(). When John.Doe@company.com views the report, he'll only see rows where Email equals "john.doe@company.com"</li>
                          <li><strong>Creating RLS Roles in Power BI Desktop:</strong> Go to Modeling tab &gt; Manage Roles &gt; Create a new role &gt; Add a table filter using USERPRINCIPALNAME() &gt; Save and publish the report</li>
                          <li><strong>Testing RLS:</strong> In Power BI Desktop, use "View as Roles" feature and enter a test email address to verify your RLS filters work correctly before publishing</li>
                          <li><strong>Common RLS Patterns:</strong> Filter by email, department, region, customer ID, or any dimension that ties users to their authorized data subsets</li>
                          <li><strong>Multiple Table Filters:</strong> You can apply RLS filters to multiple tables in your model - they work together to restrict the entire data model based on user identity</li>
                          <li><strong>Dynamic RLS:</strong> Create a security table mapping users to their authorized values, then filter using: [SecurityTable][UserEmail] = USERPRINCIPALNAME()</li>
                          <li><strong>Performance Considerations:</strong> RLS filters are applied at query time, so keep filters simple and indexed for optimal performance with large datasets</li>
                          <li><strong>No Additional Configuration Required:</strong> Once your RLS roles are defined in Power BI and the report is published, the app handles passing the correct user identity automatically</li>
                          <li><strong>Security Note:</strong> Users cannot bypass RLS filters - the effective identity is securely passed by the backend and cannot be spoofed by end users</li>
                        </ul>
                      </div>

                      <div style={{ marginBottom: '2rem' }}>
                        <h3>Subscription Information</h3>
                        <ul style={{ fontSize: '0.9rem', lineHeight: '1.6' }}>
                          <li><strong>Viewing Details:</strong> The Subscription tab shows plan information, Azure details, and term dates</li>
                          {(() => {
                            const billingModel = subscriptionData?.billingModel || subscriptionData?.BillingModel || 'PerUser';
                            const isPerUser = billingModel === 'PerUser';
                            
                            if (isPerUser) {
                              return (
                                <li><strong>Managing Your Subscription:</strong> Click "Manage in Microsoft 365" to adjust licenses and billing</li>
                              );
                            } else {
                              return (
                                <li><strong>Managing Your Subscription:</strong> Click "Manage in Azure Portal" to view or upgrade your subscription</li>
                              );
                            }
                          })()}
                        </ul>
                      </div>

                      <div>
                        <h3>Best Practices</h3>
                        <ul style={{ fontSize: '0.9rem', lineHeight: '1.6' }}>
                          <li><strong>Monitor Limits:</strong> Regularly check the User License Summary and Report License Summary to track usage</li>
                          <li><strong>License Efficiency:</strong> Deactivate licenses for inactive users to maximize availability</li>
                          <li><strong>Understanding Metrics:</strong> "Purchased" shows extra units bought, "Active" shows current usage, "Available" shows what's left</li>
                          <li><strong>Plan Upgrades:</strong> If you consistently hit limits, consider upgrading your plan for more capacity</li>
                          <li><strong>Report Organization:</strong> Use clear naming conventions for reports to simplify assignment</li>
                          <li><strong>Backup Reports:</strong> Keep local copies of PBIX files before deleting from the portal</li>
                          <li><strong>User Communication:</strong> Inform users when assigning or removing their report access</li>
                          <li><strong>Regular Reviews:</strong> Periodically audit user access and report assignments</li>
                          <li><strong>Color Coding:</strong> Watch for yellow/red indicators in summaries - they signal you're approaching or at limits</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Show message when no subscription data but no error */}
          {!loading && !dataError && !subscriptionData && !availableSubscriptions && (
            <div className="section">
              <h2>Loading Subscription Data...</h2>
              <p>Please wait while we load your subscription information.</p>
            </div>
          )}
        </div>
      </div>

      {/* Delete Report Confirmation Modal */}
      {deleteModalState.show && (
        <DeleteReportConfirmation
          reportName={deleteModalState.reportName}
          onConfirm={handleConfirmDelete}
          onCancel={handleCancelDelete}
        />
      )}

      {/* Delete Progress Popup */}
      {deleteProgress && (
        <DeleteProgressPopup
          progress={deleteProgress}
          reportName={deletingReportName}
        />
      )}

      {/* Upload Progress Popup */}
      {uploadProgress && (
        <UploadProgressPopup
          progress={uploadProgress}
          reportName={uploadingReportName}
        />
      )}

      {/* Overwrite Report Confirmation Modal */}
      {overwriteModalState.show && (
        <OverwriteReportConfirmation
          reportName={overwriteModalState.reportName}
          onConfirm={handleConfirmOverwrite}
          onCancel={handleCancelOverwrite}
        />
      )}
      
      {/* Assign Report Popup */}
      {assignReportState.show && (
        <AssignReportPopup
          reportName={assignReportState.reportName}
          reportId={assignReportState.reportId}
          users={subscriptionData?.users || []}
          onAssignReports={handleAssignReportToUsers}
          onSkip={handleSkipAssignment}
          loading={userLoading}
        />
      )}
      
      {/* Validation Error Modal */}
      {validationErrorState.show && (
        <ValidationErrorModal
          title={validationErrorState.title}
          message={validationErrorState.message}
          onClose={() => setValidationErrorState({ show: false, title: '', message: '' })}
        />
      )}
    </AuthenticatedTemplate>
  )
}

export default App

